import { useMemo } from 'preact/hooks';
import cn from 'classnames';

import { Icon } from '@nespresso-design-system/components';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import styles from './AboutRangesDrawer.module.scss';
import ModalOnDemand from '@/widgets/components/controls/ModalOnDemand/ModalOnDemand';

export type RangeInfo = {
  title: string;
  description: string;
  imageUrl?: string;
  featuresComparison?: { label: string; isIncluded: boolean }[];
};

export type AboutRangesDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  ranges: RangeInfo[];
};

const IMAGE_WIDTH = 432;

export default function AboutRangesDrawer({
  isOpen,
  onClose,
  ranges = []
}: AboutRangesDrawerProps) {
  const { t } = useTranslations();
  const headingText = t('productDetailsHero.aboutRangesDrawer.title');
  const showImages = useMemo(
    () => ranges.every((range) => range.imageUrl),
    ranges
  );

  return (
    <ModalOnDemand
      heading={headingText}
      position="side"
      isOpen={isOpen}
      onClose={onClose}
      closeBtnA11yLabel={t('components.modal.closeBtnA11yLabel')}
    >
      <div tabIndex={0} role="region" aria-label={headingText}>
        {ranges.map(({ title, description, imageUrl, featuresComparison }) => (
          <section
            class={cn(styles.container, { [styles.withImages]: showImages })}
          >
            {showImages && imageUrl && (
              <div class={styles.image}>
                <img
                  src={`${imageUrl}?impolicy=small&imwidth=${IMAGE_WIDTH}`}
                  alt="" // No extra info is given by the image
                  loading="lazy"
                />
              </div>
            )}
            <h3>{title}</h3>
            <p>{description}</p>
            {featuresComparison && featuresComparison.length > 0 && (
              <ul class={styles.featuresComparison}>
                {featuresComparison.map(({ label, isIncluded }) => (
                  <li class={styles.list}>
                    <Icon
                      method="mask"
                      icon={
                        isIncluded
                          ? '24/symbol/status-success'
                          : '24/symbol/status-canceled'
                      }
                      className={isIncluded ? styles.included : styles.excluded}
                      title={
                        isIncluded
                          ? t(
                              'productDetailsHero.aboutRangesDrawer.featureIncludedLabel'
                            )
                          : t(
                              'productDetailsHero.aboutRangesDrawer.featureExcludedLabel'
                            )
                      }
                    />
                    <span
                      class={cn(styles.featureLabel, {
                        [styles.included]: isIncluded,
                        [styles.excluded]: !isIncluded
                      })}
                    >
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </ModalOnDemand>
  );
}
